<template>
	<div class="messages shadow-left" :class="{ focused: store.appVisible }">
		<LoadingBar v-show="loadingMessages" />

		<AccessibleSection v-if="showPinnedBanner" name="pinned-message">
			<PinnedMessage @click="pinnedBannerClick" />
		</AccessibleSection>

		<AccessibleSection
			name="chat-messages"
			class="message-list-wrapper"
			focusOnFirstItem
			:tabOrder="[{ css: '.accessible-item' }]"
			:class="{ 'no-scroll': loadingMessages && messages.length === 0 }"
		>
			<UserListDrawer
				v-if="showListDrawer"
				:userIds="userList.ids"
				:label="userList.label"
				@close="showListDrawer = false"
			/>
			<MessageList
				:messages="messages"
				:isLoadingMore="messagesLoaded && loadingMessages && initialLoad"
				:readReceiptsByMessageId="readReceiptsByMessageId"
				class="message-list-main"
				:class="{ 'hide-chat': threadId }"
				:isListVisible="true"
				:allowLoadForward="!forwardHistoryComplete"
				:lastViewedId="lastViewedId"
				:isJumpingToLatest="isJumpingToLatest"
				:isLoading="loadingMessages && messages.length === 0"
				@ref="messageList = $event"
				@load-backward="handleLoadBackward"
				@load-forward="handleLoadForward"
				@thread="openThread"
				@reply="openReplyThread"
				@jump-to-latest="jumpToLatest"
				@viewed-all-unreads="dismissUnreadBanner"
				:historyComplete="historyComplete"
			/>
			<ThreadView
				v-if="threadId"
				:threadId="threadId"
				:bubbleId="store.currentChatId"
				:typingUsers="typingUsers"
				:pinView="pinView"
				@close="threadId = null"
				@reply="openReplyThread"
			/>
		</AccessibleSection>

		<div class="status-bar-wrapper">
			<div
				class="status-bar"
				:class="{ 'status-bar-expanded': !threadId && showTypingIndicator }"
				v-text="typingText"
			/>
		</div>

		<AccessibleSection
			name="chat-input"
			:tabOrder="[
				{
					css: '.media-container',
				},
				{
					css: '.pronto-editor',
					clickOnEnter: true,
					Enter: () => {
						focusEditorExitAccessibility()
					},
				},
				{
					css: '.message-input-actions .btn-file-upload',
					clickOnEnter: true,
				},
				{
					css: '.message-input-actions .emojis-icon',
				},
				{
					css: '.message-input-actions .gifs .giphy-button',
					clickOnEnter: true,
				},
			]"
			class="message-footer shadow-left"
		>
			<MessageInput
				:parentId="threadId"
				@keyup="typingHandler"
				@messageSent="messageSentHandler"
				@messageCreated="messageCreatedHandler"
				ref="messageInput"
			/>
		</AccessibleSection>

		<NewMessageButton
			v-if="showUnreadBanner"
			@click="scrollToLatestUnread"
			@dismiss="dismissUnreadBanner"
			:new-message-loader="newMessageLoader"
			:message_num="unreadCount"
			:class="{ 'pinned-banner': showPinnedBanner }"
		/>

		<div
			aria-live="assertive"
			role="status"
			ref="screenreader"
			style="opacity: 0; position: absolute; pointer-events: none;"
		/>

		<div v-if="showFail" class="error-wrapper">
			<div class="error-alert">
				<span>{{ alertText }}</span
				><span
					v-if="showRefresh"
					style="
						text-decoration: underline;
						cursor: pointer;
						margin-left: 10px;
					"
					alt="retry"
					@click="reloadPage()"
					>{{ $t('chat_fail_retry') }}</span
				>
				<input
					type="image"
					src="/images/common/white-icon-close.png"
					@click="clearFail()"
				/>
			</div>
		</div>

		<NewMeetingPopup
			v-if="showMeetingPopup"
			@close="showMeetingPopup = false"
		/>

		<SuperGroupModal
			v-if="showSuperGroupModal"
			:chatId="id"
			@close="dismissSuperGroupAlert"
		/>
	</div>
</template>

<script>
import store, {
	getMessages,
	getMessageIds,
	deleteMessage,
	getOldestMessage,
	setMessage,
	setMessages,
	updateMessage,
	getMemberships,
	getMyMembershipForBubble,
	updateMembershipForUser,
	isMyUser,
	deleteOldestMessagesForBubble,
	getMostRecentMessage,
	getMyUserId,
	getStartCursorForBubble,
	getStopCursorForBubble,
	setMessageAndMoveCursor,
	getSortedMessageIdsForBubble,
	setStopCursorForBubble,
	setStartCursorForBubble,
	importMessagesFromCacheForBubble,
	getChats,
	getChat,
	getCurrentChat,
	setVideosession,
	updateStat,
	getVideosession,
	getStat,
	getMyUser,
	getMessage,
} from '@/store'
import { post, getCancelToken, isCanceled } from '@/helpers/api'
import { closeNotificationsForBubble } from '@/helpers/pushNotification'

import MessageInput from '@/components/MessageInput'
import NewMessageButton from '@/components/NewMessageButton'
import LoadingBar from '@/components/LoadingBar'
import MessageList from './MessageList'
import PinnedMessage from './PinnedMessage'
import ThreadView from './ThreadView'
import NewMeetingPopup from '@/components/Meeting/NewMeetingPopup'
import SuperGroupModal from '@/components/Home/SuperGroupModal'
import {
	asyncIsElementInView,
	getTypingIndicatorText,
	redirectToLastActiveGroup,
	redirectToRecentsOrChat,
	getErrorText,
} from '@/helperMethods'
import debounce from 'debounce'
import {
	subscribeToPrivateBubble,
	unsubscribeFromAllPrivateBubbleChannels,
	bindAppEvents,
	bindClientEvents,
} from '@/helpers/pusher'
import {
	fetchHistory,
	fetchCenteredHistory,
	fetchHistoryWithoutStore,
	fetchMyMembershipForChat,
} from '@/api/chat'
import { markChatAsRead, updateMembership } from '@/api/membership'
import {
	MESSAGE_PAGE_SIZE,
	USER_TYPING_EVENT_INTERVAL_MS,
} from '@/helpers/constants'
import UserListDrawer from '@/components/Message/UserListDrawer'
import { sleep, storage, sStorage, trackEvent } from '@/helpers/utils'
import { AccessibleSection } from '@/components/common/Accessible'
import { closeAllPopovers } from '../common/Popover'
import { timedLogger } from '@/helpers/log'
import { updateMessageWithPoll } from '@/store/messages'

export default {
	components: {
		MessageInput,
		NewMessageButton,
		LoadingBar,
		NewMeetingPopup,
		MessageList,
		ThreadView,
		UserListDrawer,
		AccessibleSection,
		SuperGroupModal,
		PinnedMessage,
	},

	props: ['groupSettingsOpen'],

	data() {
		return {
			store,
			id: 0,
			messages: [],
			threadId: null,
			filedatatest: null,
			mark: 0,
			initialMark: null,
			loaded: false,
			messagesLoaded: true,
			loadingMessages: true,
			historyComplete: false,
			pusher: {
				channelName: null,
				channelCode: null,
				channel: null,
			},
			typingText: null,
			typingUsers: {},
			showTypingIndicator: false,
			lastTypingSentTime: 0,
			unreadCount: 0,
			currentMark: 0,
			latest_message_id: 0,
			newMessageLoader: false,
			showListDrawer: false,
			userList: null,
			loadHistoryCancelToken: null,
			loadInfoCancelToken: null,
			showFail: false,
			showRefresh: false,
			alertText: false,
			connectedToPusher: false,
			initialLoad: false,
			showMeetingPopup: false,
			readReceiptsByMessageId: {},
			centeredId: null,
			forwardHistoryComplete: false,
			hasPreviouslyConnected: false,
			isJumpingToLatest: false,
			showSuperGroupModal: false,
		}
	},

	emits: ['loaded'],

	watch: {
		groupSettingsOpen: function (val, oldVal) {
			if (oldVal && !val) {
				this.updateMark()
			}
		},

		'store.appVisible': {
			handler() {
				if (this.store.appVisible && this.loaded && !this.groupSettingsOpen) {
					this.updateMark()
				}
			},
			deep: true,
		},

		'store.userIsActive': {
			handler(val) {
				if (val && !this.groupSettingsOpen) {
					this.updateMark()
				}
			},
			deep: true,
		},

		currentChat: function (val, oldVal) {
			if (this.store.currentChatId === this.id) {
				this.$nextTick(() => {
					if (oldVal) {
						if (val.id === oldVal.id) {
							if (val.channelcode !== oldVal.channelcode) {
								this.$pusher.unsubscribe(this.channelName)
								this.connectPusher(() => {
									console.debug('reconnecting to new pusher channel')
								})
							}
						}
					}
				})
			}
		},

		threadId(id, old) {
			if (old && !id) {
				// thread view was just closed
				EventBus.$emit('clear-prepend-mention')
				this.updateMark()
			}
			EventBus.$emit('thread-change', id)
		},

		initialLoad(loaded) {
			if (loaded) {
				this.messageList.scrollTop = 0
			}
		},

		showListDrawer(show) {
			if (!show) {
				this.userList = null
			}
		},
		typingUsers: {
			handler(typingUsers) {
				const userIds = Object.keys(typingUsers)
				if (userIds.length) {
					this.showTypingIndicator = true
					this.typingText = getTypingIndicatorText(userIds)
				} else {
					this.showTypingIndicator = false
					setTimeout(() => {
						// delay for better animation
						this.typingText = null
					}, 300)
				}
			},
			deep: true,
		},
	},

	computed: {
		currentChat() {
			return getCurrentChat()
		},

		lastViewedId() {
			if (!this.initialMark || !this.latest_message_id) return null
			if (this.initialMark === this.latest_message_id) return null
			return this.initialMark
		},

		showUnreadBanner() {
			return !this.threadId && this.unreadCount > 0
		},

		showPinnedBanner() {
			return (
				!!getChat(this.store.currentChatId)?.pinned_message_id &&
				!this.threadId &&
				this.messages.length > 0 &&
				!this.hidePinnedChat
			)
		},

		hidePinnedChat() {
			const membership = getMyMembershipForBubble(this.id)
			return membership.pinned_message_hidden || false
		},

		pinView() {
			return (
				getChat(this.store.currentChatId)?.pinned_message_id === this.threadId
			)
		},
	},

	created() {
		if (!['pinned', 'recents', 'chat'].includes(this.$route.params.chat)) {
			redirectToLastActiveGroup()
			return
		}

		this.readReceiptsByMessageId = this.calculateReadReceipts()
		this.$onEventBus({
			changeBubble: ({ id }) => {
				this.load(id)
			},
			newMeeting: () => {
				this.showMeetingPopup = true
			},
			'message-change': () => {
				this.messages = getMessages()
				this.updateMark()
			},
			'message-change|mark-update|changeBubble|membership-update': debounce(
				() => {
					this.$nextTick(() => {
						this.readReceiptsByMessageId = this.calculateReadReceipts()
					})
				},
				25
			),
			'user-typing': (data) => this.userTypingHandler(data),
			'unread-banner': (count) => {
				this.unreadCount = count
			},
			'stat-update': (stat) => {
				if (stat.bubble_id !== this.id) return
				Object.assign(this, {
					currentMark: stat.mark,
					latest_message_id: stat.latest_message_id,
				})
				if (!store.appVisible) {
					this.checkIfUnreadBannerShouldDisplay()
				}
			},
			reloadPusherInstance: () => {
				this.connectPusher()
			},
			'open-pinned-message': () => {
				closeAllPopovers()
				this.pinnedBannerClick()
			},
		})

		this.chatInfoError = 0
		this.bubbleHistoryError = 0

		this.apiWatchdogInterval = setInterval(() => {
			// check fo see if any previous api calls have failed (possibly due to network issues)
			// and retry any that may have failed
			if (this.chatInfoError > 0) {
				trackEvent('chat_info_retry', { count: this.chatInfoError })
				console.debug('Chat info error, retrying...', this.chatInfoError)
				this.loadChatInfo()
			}
			if (this.bubbleHistoryError > 0) {
				trackEvent('bubble_history_retry', { count: this.bubbleHistoryError })
				console.debug(
					'Bubble history error, retrying...',
					this.bubbleHistoryError
				)
				this.loadMessageHistory()
			}
		}, 10000)
	},

	mounted() {
		if (this.store.auth.user.hasactivity) {
			// checking for any orphaned pusher channels and unsubscribing from them
			unsubscribeFromAllPrivateBubbleChannels()
		}

		if (window.todesktop) {
			const groupId = storage.get('desktop-last-group-id')

			if (groupId && !window.location.href.includes(`/${groupId}`)) {
				redirectToRecentsOrChat(groupId)
			}
		}

		this.$nextTick(() => {
			// COMMENTED THIS OUT DUE TO DUPE PUSHER EVENTS FIRING
			this.load(this.$route.params.id)
		})

		this.$pusher.connection.bind('connected', this.pusherReconnect)

		this.$onEventBus({
			focusMessageInput: () => {
				this.$refs.messageInput.setFocus()
			},
			updateUserList: (data) => {
				this.updateUserList(data)
			},
		})

		if (!this.messages.length) {
			this.messages = getMessages()
		}
	},

	beforeRouteUpdate(to, from, next) {
		const isSameRoute = to.params.id === from.params.id
		const isSameCentered = to.query.centered === from.query.centered
		if (isSameRoute && isSameCentered) return next()
		this.load(to.params.id, to.query)
		next()
	},

	beforeUnmount() {
		store.currentChatId = null
		clearInterval(this.apiWatchdogInterval)
		document.removeEventListener('keyup', this.handleKeypress)
		this.unload()
	},

	methods: {
		sleep,

		calculateReadReceipts() {
			const messageIds = getMessageIds()
			if (!messageIds.length) return {}
			const memberships = getMemberships()
			// reduce all memberships to an object of userIds by markId
			const userIdsByMessageId = memberships.reduce((acc, memb) => {
				// don't show any receipts for my user
				if (memb.user_id === store.auth.user.id) return acc
				// otherwise, get add my id to the list of users for the given mark
				acc[memb.mark] = (acc[memb.mark] || []).concat(memb.user_id)
				return acc
			}, {})
			// TODO: for increased performance, I think there's a way to combine this
			// reduce loop 👇 together with the following forEach loop but not doing
			// that now simply to avoid more bugs
			const receiptsForKnownMessages = messageIds.reduce((acc, msgId) => {
				// reduce all messages down to an object of receipts by messageId
				acc[msgId] = Object.values(userIdsByMessageId[msgId] || {})
				return acc
			}, {})
			// now loop through all other read receipts and account for ones
			// that are for messages we don't currently know about (deleted)
			Object.entries(userIdsByMessageId).forEach(([msgId, receipts]) => {
				// skip any ids we've already accounted for
				if (receiptsForKnownMessages[msgId]) return
				// find the index in messageIds for the next known message after this one
				const nextKnownMsgIndex = messageIds.findIndex((id) => id > msgId)
				// calculate the index for the closest previous known message
				let indexOfIdToAppendTo = 0
				if (nextKnownMsgIndex === -1) {
					// if there isn't a known message after this one, use the last message
					indexOfIdToAppendTo = messageIds.length - 1
				} else if (nextKnownMsgIndex > 1) {
					// otherwise use the known message right before
					indexOfIdToAppendTo = nextKnownMsgIndex - 1
				} else if (nextKnownMsgIndex === 0) {
					// if for some reason our index is 0 (user has deleted all messages), we shouldn't calculate the mark.
					return
				}
				// grab the id for that index and then append all of these
				// receipts to the end of that message
				const idToAppendTo = messageIds[indexOfIdToAppendTo]
				receiptsForKnownMessages[idToAppendTo].push(...Object.values(receipts))
			})
			return receiptsForKnownMessages
		},

		async focusEditorExitAccessibility() {
			this.accessibilityMode = false
			await sleep(100)
			this.setMessageInputFocus()
		},

		async jumpToLatest() {
			this.isJumpingToLatest = true
			await sleep(1)
			const { latest_message_id } = getStat(this.id)
			const latestMessage = getMessage(latest_message_id)
			if (!latestMessage) {
				this.loadingMessages = true
				const latest = await fetchHistoryWithoutStore({ bubble_id: this.id })
				setMessages(latest.messages)
				setMessages(latest.parentmessages)
			}
			this.centeredId = null
			this.forwardHistoryComplete = true
			// set the stop cursor to the latest message
			setStopCursorForBubble(this.id, latest_message_id)
			// wait for latest messages to render
			await this.$nextTick()
			const latestMsgElem = document.getElementById(
				`message_${latest_message_id}`
			)
			if (latestMsgElem) {
				// if possible, smoothly scroll to the bottom
				latestMsgElem.scrollIntoView({ behavior: 'smooth' })
			} else {
				// as a fallback, immediately scroll all the way to the bottom
				this.messageList.scrollTop = 0
			}
			// lastly, clean up the old search result messages
			deleteOldestMessagesForBubble(this.id)
			const [startId] = getSortedMessageIdsForBubble(this.id)
			setStartCursorForBubble(this.id, startId)
			await sleep(100)
			// waiting until message stream rerenders before turning off loaders
			this.loadingMessages = false
			this.isJumpingToLatest = false
		},

		openThread(message) {
			this.threadId = message.threadId
			if (!this.accessibilityMode) EventBus.$emit('focus-editor')
		},

		openReplyThread(message) {
			EventBus.$emit('prepend-mention', message.user_id)
			this.openThread(message)
		},

		async handleLoadBackward() {
			await this.$nextTick()
			if (!this.loadingMessages && this.loaded && !this.isJumpingToLatest) {
				this.loadNextPageOfHistory()
			}
		},

		async handleLoadForward() {
			if (
				this.forwardHistoryComplete ||
				this.loadingMessages ||
				!this.loaded ||
				!this.messages.length
			)
				return

			this.loadingMessages = true
			this.initialLoad = true
			this.messagesLoaded = true

			try {
				const origScrollHeight = this.messageList.scrollHeight
				const oldest = getStopCursorForBubble(this.id)
				const { messages } = await fetchHistory({
					bubble_id: this.id,
					oldest,
				})
				// now we have to fix the scroll position so that we don't
				// scroll down when the messages appear
				await this.$nextTick()
				const newScrollTop = origScrollHeight - this.messageList.scrollHeight
				this.messageList.scrollTop = newScrollTop
				if (messages.length < MESSAGE_PAGE_SIZE) {
					setTimeout(() => {
						// delay setting this flag so jump-to-latest doesn't disappear
						this.forwardHistoryComplete = true
					}, 100)
				}
			} catch (e) {
				console.error(e)
			} finally {
				this.loadingMessages = false
				this.initialLoad = false
				this.messagesLoaded = false
			}
		},

		async connectPusher() {
			if (!this.store.auth.user.hasactivity) {
				return
			}

			try {
				const channel = await subscribeToPrivateBubble(this.id)
				this.channelName = channel.name

				channel.bind('unavailable', this.pusherDisconnect)

				bindAppEvents({
					MessageAdded: this.messageAddedHandler.bind(this),
					ReactionAdded: this.reactionAddedHandler.bind(this),
					ReactionRemoved: this.reactionRemovedHandler.bind(this),
					MessageUpdated: this.messageUpdatedHandler.bind(this),
					MessageRemoved: this.messageRemovedHandler.bind(this),
					MessageTransAdded: this.messageTransAddedHandler.bind(this),
					MarkUpdated: this.markUpdatedHandler.bind(this),
					VideosessionUpdated: this.videosessionUpdatedHandler.bind(this),
					PollUpdated: this.pollUpdatedHandler.bind(this),
				})

				bindClientEvents({
					UserTyping: this.userTypingHandler.bind(this),
					UserStoppedTyping: this.userStoppedTypingHandler.bind(this),
				})

				this.connectedToPusher = true
			} catch (e) {
				this.connectedToPusher = false
				console.error(e)
			}
		},

		pusherDisconnect() {
			console.debug(
				'Diconnect to private bubble channel in chat.vue - will attempt to reconnect'
			)
			this.load(this.$route.params.id)
		},

		pusherReconnect() {
			if (this.hasPreviouslyConnected) {
				this.load(this.$route.params.id)
			}
			this.hasPreviouslyConnected = true
		},

		async load(id, toQuery) {
			if (isNaN(parseInt(id))) {
				console.debug(`Load function passed bad ID ${id}`)
				redirectToLastActiveGroup()
				return
			}
			this.unload()

			// check for a ?centered=id query param
			const { centered } = toQuery || this.$route.query

			if (centered) {
				this.centeredId = parseInt(centered)

				const url = new URL(location)
				url.searchParams.delete('centered')
				history.replaceState({}, null, url)
			} else {
				this.forwardHistoryComplete = true
			}

			this.id = parseInt(id)

			if (!this.centeredId) {
				store.dbLoadChat(parseInt(id))

				setTimeout(() => {
					this.messagesLoaded = false
					this.$nextTick(() => {
						setTimeout(() => {
							this.initialLoad = true
						}, 1000)
					})
				}, 1000)
			}

			this.$refs.messageInput?.setFocus()

			// Wait until the list of bubbles is loaded before we try to load this bubble
			if (getChats().length == 0) {
				setTimeout(() => {
					this.load(id)
				}, 100)
				return
			}

			var currentChat = getChat(this.id)
			this.currentMark = 0
			this.threadId = null

			if (!currentChat || !Object.keys(currentChat).length) {
				// Can't find our id in the list of bubbles, go to the first bubble
				// emit an event to the Home component
				this.$router.push('/chat/' + getChats()[0].id)
				return
			}

			this.store.currentChatId = this.id
			const stat = getStat(this.id)
			Object.assign(this, {
				initialMark: stat.mark,
				latest_message_id: stat.latest_message_id,
			})

			this.loadingMessages = true

			await this.connectPusher()

			// We're setup for this bubble except for message history
			this.loaded = true

			this.loadChatInfo()

			if (this.centeredId) {
				await this.loadCenteredHistory(this.centeredId)
			} else {
				// Load message history
				await this.loadMessageHistory()
			}

			this.loadingMessages = false

			this.$emit('loaded')

			storage.set('lastActiveGroupId', this.id)

			//check to see if this bubble has someone that's currently live
			if (currentChat.videosession_id) {
				const videosession = getVideosession(currentChat.videosession_id)
				if (videosession && videosession.status == 'ACTIVE') {
					this.store.livestreaming.videosession = videosession
				}
			}

			if (!sStorage.has(`pronto_marked_unread_${this.id}`)) {
				const chat = getChat(this.id)
				if (chat?.marked_unread) {
					markChatAsRead(chat.id)
				}
			}

			this.checkForSuperGroupAlert()
		},

		unload() {
			this.messagesLoaded = true
			this.initialLoad = false
			this.historyComplete = false
			this.forwardHistoryComplete = false
			this.centeredId = null
			this.initialMark = null
			this.unreadCount = 0
			deleteOldestMessagesForBubble(this.id)
			this.messages = []
			this.showListDrawer = false

			if (this.store.auth.user.hasactivity) {
				// unsubscribe pusher
				this.$pusher.unsubscribe(this.channelName)
				// this is hacky logic to clean up oprhaned channels.  Pusher's library hangs onto these ever after they've been cleaned up.
				const channel = this.$pusher.channel(this.channelName)
				if (channel) {
					channel.subscribed = true
					channel.subscriptionPending = false
					channel.subscriptionCancelled = false
					this.$pusher.unsubscribe(channel.name)
				}
			}

			Object.values(this.typingUsers).forEach(({ timeout }) => {
				window.clearTimeout(timeout)
			})

			this.typingUsers = {}

			this.loaded = false
			this.loadingMessages = false
		},

		async loadChatInfo() {
			try {
				let chatId = this.id

				this.loadInfoAbortController?.abort()
				this.loadInfoAbortController = new AbortController()

				const membership = await fetchMyMembershipForChat(
					chatId,
					{},
					{
						signal: this.loadInfoAbortController.signal,
					}
				)
				const { chat, stats } = membership
				if (chat?.id !== chatId) return false

				Object.assign(this, {
					unread: stats.unread,
					latest_message_id: stats.latest_message_id,
					initialMark: stats.mark,
					currentMark: stats.mark,
				})
				this.chatInfoError = 0
			} catch (e) {
				if (isCanceled(e)) return
				this.chatInfoError += 1
			}
		},

		async loadCenteredHistory(messageId) {
			try {
				const { latest_message_id } = getStat(this.id)
				const { older, newer } = await fetchCenteredHistory(this.id, messageId)
				updateMessage(messageId, { isHighlighted: true })
				await sleep(250) // delay to wait for store to update so highlighted message is visible
				this.loadingMessages = false
				importMessagesFromCacheForBubble(this.id, true)
				if (older.messages.length < MESSAGE_PAGE_SIZE) {
					this.historyComplete = true
				}
				// check if we've loaded the full history
				this.forwardHistoryComplete =
					(newer.messages.length < 1 ||
						newer.messages[0]?.id === latest_message_id) ??
					false
				// center the centered message
				const el = document.getElementById(`message_${messageId}`)
				el?.scrollIntoView({
					behavior: 'auto',
					block: 'center',
					inline: 'center',
				})
				await this.$nextTick()
				// fetch latest history so we are up to date if user jumps
				// to latest but don't update cursors
				const latest = await fetchHistoryWithoutStore({
					bubble_id: this.id,
					latest: latest_message_id,
				})
				setMessages(latest.messages)
				setMessages(latest.parentmessages)
			} catch (e) {
				console.error(e)
			}
		},

		async loadNextPageOfHistory() {
			this.loadingMessages = true
			const start = getStartCursorForBubble(this.id)
			await this.loadMessageHistory(start)
			this.loadingMessages = false
		},

		async loadMessageHistory(latest) {
			if (this.historyComplete) {
				return
			}

			let bubbleId = this.id
			let data = { bubble_id: bubbleId }

			const log = timedLogger(`bubble history ${bubbleId}`)

			if (!this.messageList) {
				setTimeout(() => {
					this.loadMessageHistory()
				}, 500)
				return
			}

			if (latest) {
				data.latest = latest
			}

			// TODO: we should cancel any history requests if the currentBubble changes:
			// https://github.com/axios/axios#cancellation

			if (this.loadHistoryCancelToken) {
				this.loadHistoryCancelToken.cancel()
				this.loadHistoryCancelToken = null
			}

			this.loadHistoryCancelToken = getCancelToken()

			try {
				log('starting fetch...')
				const { messages } = await fetchHistory(
					data,
					this.loadHistoryCancelToken
				)
				log('fetch complete')
				if (bubbleId != this.id) {
					return
				}

				if (messages.length == 0 || messages.length < MESSAGE_PAGE_SIZE) {
					this.historyComplete = true
				}

				this.messagesLoaded = true

				this.checkIfUnreadBannerShouldDisplay()

				this.updateMark()
				this.bubbleHistoryError = 0
			} catch (error) {
				if (isCanceled(error)) return
				log('history fail')
				console.error('BUBBLE.HISTORY ERROR', error)

				if (error.response?.data?.error === 'BUBBLE_NOTFOUND') {
					if (getChats().length > 0) {
						this.$router.push('/chat/' + getChats()[0].id)
					} else {
						this.$router.push({ name: 'home' })
					}
					return
				}

				this.bubbleHistoryError += 1
			}
			log.print()
		},

		checkForSuperGroupAlert() {
			if (!this.id) return
			const chat = getChat(this.id)
			const membership = getMyMembershipForBubble(this.id)
			if (chat.is_supergroup && !membership?.supergroup_alert_seen) {
				setTimeout(() => {
					this.showSuperGroupModal = true
				}, 1000)
			}
		},
		async dismissSuperGroupAlert() {
			this.showSuperGroupModal = false
			const chat = getChat(this.id)
			const membership = getMyMembershipForBubble(this.id)
			if (chat.is_supergroup && !membership?.supergroup_alert_seen) {
				await updateMembership({
					bubble_id: this.id,
					supergroup_alert_seen: true,
				})
			}
		},

		async checkIfUnreadBannerShouldDisplay() {
			const { threadId, messageList } = this
			const stat = getStat(this.id)
			// if still loading, if no unreads or if in thread view, hide banner
			if (!stat?.unread || threadId) return false
			const { unread } = stat
			await this.$nextTick() // ensure message stream has fully rendered
			const unreadDivider = document.getElementById('new-msg-divider')
			// if the unread divider has not been rendered, bail
			if (!unreadDivider) return false
			// if the latest unread message is already in view, no need to show banner
			if (await asyncIsElementInView(unreadDivider, messageList)) return false
			console.debug(
				`CHAT: displaying unread banner: ${unread}, mark is ${stat.mark}`
			)
			this.unreadCount = unread
			return true
		},

		dismissUnreadBanner() {
			console.debug('CHAT: dismissing unread banner')
			const stat = getStat(this.id)
			Object.assign(this, {
				unreadCount: 0,
				initialMark: stat.mark,
				newMessageLoader: false,
			})
		},

		async scrollToLatestUnread() {
			let unreadDivider = document.getElementById('new-msg-divider')
			if (!unreadDivider) {
				// the most likely scenario for why we don't have a new-msg-divider is
				// because the oldest unread message is so far back in the history that we
				// need to try to load it asynchronously and then scroll to it
				this.newMessageLoader = true
				await this.loadCenteredHistory(this.initialMark)
				await this.$nextTick()
				unreadDivider = document.getElementById('new-msg-divider')
			}
			this.dismissUnreadBanner()
			unreadDivider?.scrollIntoView({ behavior: 'smooth' })
		},

		updateUserList(data) {
			this.userList = data
			this.showListDrawer = true
		},

		messageSentHandler() {
			if (!this.channelName) return
			const channel = this.$pusher.channel(this.channelName)
			channel?.trigger('client-App\\Events\\UserStoppedTyping', {
				user_id: getMyUserId(),
			})
		},

		messageCreatedHandler() {
			this.$refs.screenreader.innerHTML = getOldestMessage()
		},

		pollUpdatedHandler({ poll }) {
			updateMessageWithPoll(poll)
		},

		updateMark() {
			const latestMessage = getMostRecentMessage()
			const { appVisible, userIsActive } = this.store
			if (
				!this.loaded ||
				!this.messages.length ||
				this.threadId ||
				!appVisible ||
				!userIsActive ||
				!latestMessage ||
				this.currentMark >= latestMessage.id
			) {
				return
			}

			post('/api/v1/bubble.mark', {
				bubble_id: this.id,
				message_id: latestMessage.id,
			})
				.then(({ data }) => {
					if (data.ok) {
						closeNotificationsForBubble(this.id)
						if (!this.showUnreadBanner) {
							this.initialMark = latestMessage.id
						}
						if (latestMessage.id > this.currentMark) {
							this.currentMark = latestMessage.id
							updateStat(this.id, {
								unread_mentions: 0,
								mark: latestMessage.id,
							})
						}
					}
				})
				.catch((error) => {
					console.error(error)
				})
		},

		typingHandler() {
			if (!this.channelName) return
			const timeSinceLastEvent = Date.now() - (this.lastTypingSentTime || 0)
			if (timeSinceLastEvent >= USER_TYPING_EVENT_INTERVAL_MS) {
				this.lastTypingSentTime = Date.now()
				const channel = this.$pusher.channel(this.channelName)
				channel?.trigger('client-App\\Events\\UserTyping', {
					user_id: getMyUserId(),
					thread_id: this.threadId || null,
				})
			}
		},

		messageAddedHandler({ message }) {
			setMessageAndMoveCursor(message)

			if (message.bubble_id !== this.store.currentChatId) return false

			if (this.store.appVisible) {
				this.updateMark()
			}
		},

		messageUpdatedHandler({ message }) {
			setMessage(message)
		},

		reactionAddedHandler({ message_id, emoji, emoji_base, user_id, count }) {
			const me = user_id === getMyUserId()
			const message = getMessage(message_id)
			const currentReactions = { ...message.reactions }
			const existingReaction = currentReactions[emoji]

			if (existingReaction) {
				// Update existing reaction
				currentReactions[emoji] = {
					...existingReaction,
					count,
					me: me || existingReaction.me,
					users: me
						? [...existingReaction.users, user_id]
						: existingReaction.users,
					emoji,
					emoji_base,
				}
			} else {
				// Create new reaction
				currentReactions[emoji] = {
					users: [user_id],
					me,
					count,
					emoji,
					emoji_base,
				}
			}

			updateMessage(message.id, {
				reactions: currentReactions,
			})
		},

		reactionRemovedHandler({ message_id, emoji, emoji_base, user_id, count }) {
			const me = user_id === getMyUserId()
			const message = getMessage(message_id)
			const currentReactions = { ...message.reactions }

			if (count === 0) {
				// Remove emoji variant if count is 0
				delete currentReactions[emoji]
			} else {
				// Update emoji variant if it exists
				const reaction = currentReactions[emoji]
				if (reaction) {
					currentReactions[emoji] = {
						...reaction,
						count,
						me: me ? false : reaction.me,
						users: me ? [] : [user_id],
						emoji,
						emoji_base,
					}
				}
			}

			updateMessage(message.id, {
				reactions: currentReactions,
			})
		},

		messageTransAddedHandler({ messagetrans }) {
			const me = getMyUser()
			if (messagetrans.lang === me.language) {
				updateMessage(messagetrans.message_id, { messagetrans })
			}
		},

		messageRemovedHandler({ message }) {
			deleteMessage(message.id)
		},

		markUpdatedHandler({ user_id, mark }) {
			updateMembershipForUser(user_id, { mark })
		},

		userTypingHandler(payload) {
			const { user_id, thread_id } = payload
			if (isMyUser(user_id)) return

			// if we already knew about them, clear their timeout
			const { timeout } = this.typingUsers[user_id] || {}
			window.clearTimeout(timeout)

			// regardless, set a new timeout for this user
			this.typingUsers[user_id] = {
				thread_id,
				user_id,
				timeout: setTimeout(() => {
					this.userStoppedTypingHandler({ user_id })
				}, 5000),
			}
		},

		userStoppedTypingHandler({ user_id }) {
			const { [user_id]: data, ...rest } = this.typingUsers
			window.clearTimeout(data?.timeout)
			this.typingUsers = rest
		},

		videosessionUpdatedHandler({ videosession }) {
			if (videosession.id === this.store.livestreaming.videosession.id) {
				this.store.livestreaming.videosession = videosession
			}

			const msgs = this.messages.filter(
				(msg) => msg.videosession_id === videosession.id
			)

			msgs.forEach((message) => {
				updateMessage(message.id, { videosession })
			})

			if (videosession.bubble_id === store.currentChatId) {
				setVideosession(videosession)
			}
		},

		reload() {
			this.load(this.$route.params.id)
		},

		setMessageInputFocus() {
			this.$refs.messageInput.setFocus()
		},

		clearFail() {
			this.showFail = false
			this.showRefresh = false
		},

		reloadPage() {
			window.location.reload(true)
		},

		pinnedBannerClick() {
			const pinnedId = getChat(store.currentChatId).pinned_message_id
			const pinnedMsg = getMessage(pinnedId)
			pinnedMsg.threadId = pinnedId
			this.openThread(pinnedMsg)
		},
	},
}
</script>
<style lang="scss" scoped>
@import 'src/sass/variables';
.messages {
	grid-area: main;
	overflow-y: hidden;
	flex: 1;
	display: flex;
	flex-direction: column;
}

.status-bar-wrapper {
	position: relative;
}

.message-list-main {
	z-index: 1;
	opacity: 1;
	transition: all 0.5s ease-in-out;
	transition-property: opacity, filter;
	&.hide-chat {
		opacity: 0;
		filter: blur(15px);
		// don't allow clicking when threadview is shown - Fixes Safari issue
		pointer-events: none;
	}
}

.status-bar {
	left: 0;
	bottom: 1px;
	position: absolute;
	background-color: var(--neutral-0);
	object-fit: contain;
	font-family: Avenir-Roman;
	font-size: 12px;
	text-align: left;
	@include flex;
	color: var(--neutral-500);
	z-index: 2;
	transition: all 0.3s ease-in-out;
	transition-property: height, opacity;
	height: 0;
	opacity: 0;
	overflow: hidden;
	width: 100%;
	padding-left: 30px;
	&.status-bar-expanded {
		height: 25px;
		opacity: 1;
	}
}

.message-list-wrapper {
	position: relative;
	flex-grow: 1;
	overflow: auto;
	display: flex;
	border-bottom: solid 1px var(--neutral-40040);
}

.message-footer {
	flex-shrink: 0;
	background: var(--neutral-0);
}

.messages-loader {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 0;
}

.no-messages {
	position: absolute;
	top: calc(50% - 20px);
	font-family: Avenir-Medium;
	font-size: 16px;
	color: var(--neutral-400);
	left: 0;
	right: 0;
	text-align: center;
}

.no-scroll {
	overflow: hidden;
}

.pinned-banner {
	top: 135px !important;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.messages {
		.message-list-wrapper {
			background: var(--neutral-100) !important;
			.thread-message-list {
				.online-indicator {
					border-color: var(--neutral-300);
				}
				.message-wrapper {
					&.message-hover {
						.online-indicator {
							border-color: var(--neutral-0);
						}
					}
				}
			}
		}
		.status-bar {
			background: var(--neutral-100);
		}
	}
}
</style>
